import type { CharacterKind, Difficulty } from '@/components'
import { ABILITIES } from '@/game/abilities/definitions'
import type { ArenaDef } from '@/game/arenas/Arena'
import { ARENAS, ARENA_SIZES } from '@/game/arenas'
import { MAX_FIGHTERS } from '@/components'
import {
  PLAYER_COLORS,
  WIN_TARGETS,
  type Controller,
  type Settings,
} from '@/game/resources'
import { BODY_PLANS, CHARACTER_KINDS } from '@/gfx/procedural/characterKinds'
import type { AssetLibrary } from '@/gfx/assets/AssetLoader'
import { renderModelPreviews } from '@/gfx/procedural/modelPreview'
import { AI_PROFILES } from '@/systems/AiSystem'
import { el, row, wrap } from './Hud'

export type MatchMode = 'versus' | 'solo' | 'coop' | 'demo'

const MODE_LABELS: Array<[MatchMode, string]> = [
  ['versus', '1 НА 1'],
  ['solo', 'ИГРОК ПРОТИВ БОТОВ'],
  ['coop', 'ДВОЕ ПРОТИВ БОТОВ'],
  ['demo', 'ТОЛЬКО БОТЫ'],
]

/** How many human seats each mode uses. */
const MODE_HUMANS: Record<MatchMode, number> = {
  versus: 2,
  solo: 1,
  coop: 2,
  demo: 0,
}

export interface MatchConfig {
  arenaId: string
  winsNeeded: number
  controllers: Controller[]
  difficulty: Difficulty
  models: CharacterKind[]
  /** Multiplier on the arena's authored footprint. */
  arenaScale: number
  /** Team per slot; in a free-for-all each slot is its own team. */
  teams: number[]
}

export interface MenuCallbacks {
  onStart(config: MatchConfig): void
  /** «Играть по сети»: the room screens take over from here. */
  onOnline(): void
  onResume(): void
  onRestart(): void
  onQuitToMenu(): void
  onSettings(patch: Partial<Settings>): void
}

type ScreenName = 'main' | 'pause' | 'matchOver' | 'none'

/**
 * Every non-gameplay screen: title, map select, pause and the results card.
 *
 * The map cards draw their own thumbnails by sampling each arena's signed
 * distance field, so a new arena shows up in the menu with the correct silhouette
 * without anyone drawing an icon for it.
 */
export class Menu {
  readonly root: HTMLElement

  private readonly main: HTMLElement
  private readonly pause: HTMLElement
  private readonly matchOver: HTMLElement
  private readonly winnerLine: HTMLElement
  private readonly scoreLine: HTMLElement
  private readonly cards: HTMLElement[] = []
  private readonly winButtons: HTMLElement[] = []
  private readonly sizeButtons: HTMLElement[] = []
  /** A setting appears on both the main menu and the pause screen. */
  private readonly toggles = new Map<keyof Settings, HTMLElement[]>()

  private selectedArena = ARENAS[0].id
  /** Index into `ARENA_SIZES`; 1 is the authored footprint. */
  private arenaSize = 1
  private winsNeeded = 3
  private current: ScreenName = 'none'
  private mode: MatchMode = 'versus'
  private difficulty: Difficulty = 1
  private botCount = 2
  private models: CharacterKind[] = ['guard', 'beetle']
  private readonly modeButtons: HTMLElement[] = []
  private readonly difficultyButtons: HTMLElement[] = []
  private readonly botButtons: HTMLElement[] = []
  private readonly modelButtons: Array<{
    player: 0 | 1
    kind: CharacterKind
    el: HTMLElement
  }> = []
  private difficultyRow: HTMLElement | null = null
  private modelColumns: HTMLElement[] = []
  private readonly modelBlurbs: HTMLElement[] = []
  /**
   * Rendered fighter portraits, one set per human seat's colour. Empty until
   * `renderPreviews`: the menu is not shown before the models have arrived.
   */
  private readonly previews: Array<Record<CharacterKind, string>> = []

  constructor(
    parent: HTMLElement,
    private settings: Settings,
    private readonly callbacks: MenuCallbacks,
  ) {
    this.root = el('div', 'menu-root')
    this.root.style.position = 'absolute'
    this.root.style.inset = '0'
    this.root.style.pointerEvents = 'none'

    this.main = this.buildMain()
    this.pause = this.buildPause()
    const results = this.buildMatchOver()
    this.matchOver = results.screen
    this.winnerLine = results.winnerLine
    this.scoreLine = results.scoreLine

    this.root.append(this.main, this.pause, this.matchOver)
    parent.append(this.root)
    // Hidden until the game has the fighter models (see `renderPreviews`).
    this.show('none')
  }

  show(screen: ScreenName): void {
    this.current = screen
    this.main.classList.toggle('hidden', screen !== 'main')
    this.pause.classList.toggle('hidden', screen !== 'pause')
    this.matchOver.classList.toggle('hidden', screen !== 'matchOver')
  }

  get visible(): boolean {
    return this.current !== 'none'
  }

  /** `winner` is already the side's display name — a fighter's or a team's. */
  showResults(winner: string, score: number[]): void {
    this.winnerLine.textContent = `ПОБЕДА · ${winner}`
    this.scoreLine.textContent = score.join(' : ')
    this.show('matchOver')
  }

  // ────────────────────────────────────────────────────────────── screens ──

  private buildMain(): HTMLElement {
    const screen = el('div', 'screen')

    const title = el('h1', 'title')
    title.textContent = 'VOIDFALL'
    const subtitle = el('div', 'subtitle')
    subtitle.textContent = 'ДВОЕ НА ОДНОМ ЭКРАНЕ · ВЫТОЛКНИ ИЛИ УПАДИ'

    const arenaLabel = el('div', 'section-label')
    arenaLabel.textContent = 'АРЕНА'
    const cards = el('div', 'arena-cards')
    for (const def of ARENAS) {
      const card = this.buildArenaCard(def)
      cards.append(card)
      this.cards.push(card)
    }
    this.selectArena(this.selectedArena)

    const modeLabel = el('div', 'section-label')
    modeLabel.textContent = 'РЕЖИМ'
    const modeRow = el('div', 'row')
    for (const [mode, label] of MODE_LABELS) {
      const btn = el('button', 'btn small toggle')
      btn.textContent = label
      btn.dataset.mode = mode
      btn.addEventListener('click', () => {
        this.mode = mode
        this.refreshModes()
      })
      modeRow.append(btn)
      this.modeButtons.push(btn)
    }

    const difficultyRow = el('div', 'row')
    const countTag = el('div', 'inline-label')
    countTag.textContent = 'БОТОВ'
    difficultyRow.append(countTag)
    for (let count = 1; count <= MAX_FIGHTERS - 1; count++) {
      const btn = el('button', 'btn small toggle')
      btn.textContent = String(count)
      btn.addEventListener('click', () => {
        this.botCount = count
        this.refreshModes()
      })
      difficultyRow.append(btn)
      this.botButtons.push(btn)
    }

    const difficultyTag = el('div', 'inline-label')
    difficultyTag.textContent = 'СЛОЖНОСТЬ'
    difficultyRow.append(difficultyTag)
    for (const level of [0, 1, 2] as Difficulty[]) {
      const btn = el('button', 'btn small toggle')
      btn.textContent = AI_PROFILES[level].label
      btn.addEventListener('click', () => {
        this.difficulty = level
        this.refreshModes()
      })
      difficultyRow.append(btn)
      this.difficultyButtons.push(btn)
    }
    this.difficultyRow = difficultyRow

    const modelLabel = el('div', 'section-label')
    modelLabel.textContent = 'БОЙЦЫ'
    const modelRow = el('div', 'model-columns')
    this.modelColumns = [this.buildModelPicker(0), this.buildModelPicker(1)]
    modelRow.append(...this.modelColumns)
    this.refreshModes()

    const sizeLabel = el('div', 'section-label')
    sizeLabel.textContent = 'РАЗМЕР ПЛОЩАДКИ'
    const sizeRow = el('div', 'row')
    for (let i = 0; i < ARENA_SIZES.length; i++) {
      const btn = el('button', 'btn small toggle')
      btn.textContent = ARENA_SIZES[i].label
      btn.addEventListener('click', () => {
        this.arenaSize = i
        this.refreshSizeButtons()
      })
      sizeRow.append(btn)
      this.sizeButtons.push(btn)
    }
    this.refreshSizeButtons()

    const winsLabel = el('div', 'section-label')
    winsLabel.textContent = 'ПОБЕД ДЛЯ ВЫИГРЫША МАТЧА'
    const winsRow = el('div', 'row')
    for (const wins of WIN_TARGETS) {
      const btn = el('button', 'btn small toggle')
      btn.textContent = String(wins)
      btn.addEventListener('click', () => {
        this.winsNeeded = wins
        this.refreshWinButtons()
      })
      winsRow.append(btn)
      this.winButtons.push(btn)
    }
    this.refreshWinButtons()

    const settingsRow = el('div', 'row')
    settingsRow.append(
      this.buildToggle('bloom', 'СВЕЧЕНИЕ'),
      this.buildToggle('ssao', 'ЗАТЕНЕНИЕ'),
      this.buildToggle('staticCamera', 'СТАТИЧНАЯ КАМЕРА'),
    )

    const start = el('button', 'btn primary')
    start.textContent = 'НАЧАТЬ МАТЧ'
    start.addEventListener('click', () => this.callbacks.onStart(this.config()))
    const online = el('button', 'btn')
    online.textContent = 'ИГРАТЬ ПО СЕТИ'
    online.addEventListener('click', () => this.callbacks.onOnline())

    const hint = el('div', 'hint')
    hint.textContent = 'ESC — ПАУЗА · F3 — ОТЛАДКА · ГЕЙМПАД ПОДХВАТЫВАЕТСЯ НА ХОДУ'

    screen.append(
      title,
      subtitle,
      wrap(modeLabel, modeRow, difficultyRow),
      wrap(arenaLabel, cards),
      wrap(sizeLabel, sizeRow),
      wrap(modelLabel, modelRow),
      wrap(winsLabel, winsRow),
      settingsRow,
      row(start, online),
      this.buildControls(),
      hint,
    )
    return screen
  }

  /** The current selection, in the shape the game needs to start a match. */
  private config(): MatchConfig {
    const humans = MODE_HUMANS[this.mode]
    const bots =
      this.mode === 'versus' ? 0 : Math.min(this.botCount, MAX_FIGHTERS - humans)

    const controllers: Controller[] = []
    const models: CharacterKind[] = []
    for (let i = 0; i < humans; i++) {
      controllers.push('human')
      models.push(this.models[i] ?? CHARACTER_KINDS[i % CHARACTER_KINDS.length])
    }
    for (let i = 0; i < bots; i++) {
      controllers.push('ai')
      // Bots take the remaining body plans in order, so a six-way brawl has six
      // visibly different silhouettes rather than six of the same fighter.
      models.push(CHARACTER_KINDS[(humans + i) % CHARACTER_KINDS.length])
    }

    // In co-op the humans share a side and the bots share the other one; every
    // other mode is a free-for-all, where a fighter is their own team.
    const teams =
      this.mode === 'coop'
        ? controllers.map((c) => (c === 'human' ? 0 : 1))
        : controllers.map((_, i) => i)

    return {
      arenaId: this.selectedArena,
      winsNeeded: this.winsNeeded,
      controllers,
      difficulty: this.difficulty,
      models,
      arenaScale: ARENA_SIZES[this.arenaSize].scale,
      teams,
    }
  }

  /** Fighter portraits in the first slot's colour, for the online picker. */
  portraits(): Record<CharacterKind, string> {
    return this.previews[0]
  }

  /**
   * Renders the portraits, once the fighter models have loaded, so the picker
   * shows what each fighter looks like in that seat's colour rather than
   * describing it in a sentence.
   *
   * Only after the models: portraits drawn earlier came from the procedural
   * rigs, and over the network the menu showed those for seconds — one
   * character in the picker, another in the arena. A model that failed to load
   * is drawn as its rig, which is also what that fighter will be.
   */
  renderPreviews(assets: AssetLibrary): void {
    for (let seat = 0; seat < 2; seat++) {
      this.previews[seat] = renderModelPreviews(PLAYER_COLORS[seat], 150, 176, assets)
    }
    for (const button of this.modelButtons) {
      const image = button.el.querySelector('img')
      if (image) image.src = this.previews[button.player][button.kind]
    }
  }

  private buildModelPicker(player: 0 | 1): HTMLElement {
    const column = el('div', 'model-column')
    const heading = el('h4', player === 0 ? 'p1' : 'p2')
    heading.textContent = `ИГРОК ${player + 1}`
    column.append(heading)

    const grid = el('div', 'model-grid')

    for (const kind of CHARACTER_KINDS) {
      const plan = BODY_PLANS[kind]
      const btn = el('button', 'model-option clickable')

      // The picture comes with `renderPreviews`.
      const image = document.createElement('img')
      image.className = 'model-portrait'
      image.alt = plan.name

      const name = el('div', 'model-name')
      name.textContent = plan.name
      btn.append(image, name)
      btn.title = plan.blurb

      btn.addEventListener('click', () => {
        this.models[player] = kind
        this.refreshModels()
      })
      grid.append(btn)
      this.modelButtons.push({ player, kind, el: btn })
    }

    const blurb = el('div', 'model-blurb')
    column.append(grid, blurb)
    this.modelBlurbs[player] = blurb

    this.refreshModels()
    return column
  }

  private refreshModels(): void {
    for (const entry of this.modelButtons) {
      entry.el.classList.toggle('selected', this.models[entry.player] === entry.kind)
    }
    for (let player = 0; player < this.modelBlurbs.length; player++) {
      const blurb = this.modelBlurbs[player]
      const kind = this.models[player]
      if (blurb && kind) blurb.textContent = BODY_PLANS[kind].blurb
    }
  }

  private refreshModes(): void {
    for (const btn of this.modeButtons) {
      btn.classList.toggle('on', btn.dataset.mode === this.mode)
    }
    for (let i = 0; i < this.difficultyButtons.length; i++) {
      this.difficultyButtons[i].classList.toggle('on', i === this.difficulty)
    }

    const humans = MODE_HUMANS[this.mode]
    const maxBots = MAX_FIGHTERS - humans
    for (let i = 0; i < this.botButtons.length; i++) {
      const count = i + 1
      this.botButtons[i].classList.toggle('on', count === this.botCount)
      // A six-fighter cap means the available bot counts depend on the mode.
      this.botButtons[i].classList.toggle('hidden', count > maxBots)
    }
    if (this.botCount > maxBots) this.botCount = maxBots

    // Options that mean nothing in this mode disappear rather than sitting
    // greyed out: no bots in a duel, no fighter picker with no humans.
    if (this.difficultyRow) {
      this.difficultyRow.style.display = this.mode === 'versus' ? 'none' : 'flex'
    }
    for (let i = 0; i < this.modelColumns.length; i++) {
      this.modelColumns[i].style.display = i < humans ? 'flex' : 'none'
    }
  }

  private buildPause(): HTMLElement {
    const screen = el('div', 'screen hidden')
    const title = el('h1', 'title')
    title.style.fontSize = '46px'
    title.textContent = 'ПАУЗА'

    const row = el('div', 'row')
    const resume = el('button', 'btn primary')
    resume.textContent = 'ПРОДОЛЖИТЬ'
    resume.addEventListener('click', () => this.callbacks.onResume())

    const restart = el('button', 'btn')
    restart.textContent = 'РЕСТАРТ РАУНДА'
    restart.addEventListener('click', () => this.callbacks.onRestart())

    const quit = el('button', 'btn')
    quit.textContent = 'В МЕНЮ'
    quit.addEventListener('click', () => this.callbacks.onQuitToMenu())

    row.append(resume, restart, quit)

    const settingsRow = el('div', 'row')
    settingsRow.append(
      this.buildToggle('bloom', 'СВЕЧЕНИЕ'),
      this.buildToggle('ssao', 'ЗАТЕНЕНИЕ'),
      this.buildToggle('staticCamera', 'СТАТИЧНАЯ КАМЕРА'),
    )

    screen.append(title, row, settingsRow, this.buildControls())
    return screen
  }

  private buildMatchOver(): {
    screen: HTMLElement
    winnerLine: HTMLElement
    scoreLine: HTMLElement
  } {
    const screen = el('div', 'screen hidden')
    const winnerLine = el('div', 'winner-line')
    const scoreLine = el('div', 'score-line')

    const row = el('div', 'row')
    const rematch = el('button', 'btn primary')
    rematch.textContent = 'РЕВАНШ'
    rematch.addEventListener('click', () => this.callbacks.onStart(this.config()))
    const menu = el('button', 'btn')
    menu.textContent = 'В МЕНЮ'
    menu.addEventListener('click', () => this.callbacks.onQuitToMenu())
    row.append(rematch, menu)

    screen.append(winnerLine, scoreLine, row)
    return { screen, winnerLine, scoreLine }
  }

  // ──────────────────────────────────────────────────────────── fragments ──

  private buildArenaCard(def: ArenaDef): HTMLElement {
    const card = el('button', 'arena-card clickable')
    const thumb = renderArenaThumbnail(def)
    thumb.className = 'arena-shape'

    const name = el('h3')
    name.textContent = def.name
    const blurb = el('p')
    blurb.textContent = def.blurb

    card.append(thumb, name, blurb)
    card.addEventListener('click', () => this.selectArena(def.id))
    return card
  }

  private selectArena(id: string): void {
    this.selectedArena = id
    for (let i = 0; i < this.cards.length; i++) {
      this.cards[i].classList.toggle('selected', ARENAS[i].id === id)
    }
  }

  private refreshWinButtons(): void {
    for (let i = 0; i < this.winButtons.length; i++) {
      this.winButtons[i].classList.toggle('on', WIN_TARGETS[i] === this.winsNeeded)
    }
  }

  private refreshSizeButtons(): void {
    for (let i = 0; i < this.sizeButtons.length; i++) {
      this.sizeButtons[i].classList.toggle('on', i === this.arenaSize)
    }
  }

  private buildToggle(key: keyof Settings, label: string): HTMLElement {
    const btn = el('button', 'btn small toggle')
    btn.textContent = label
    btn.classList.toggle('on', Boolean(this.settings[key]))

    btn.addEventListener('click', () => {
      const next = !this.settings[key]
      // Mutate the shared object rather than forking it: the Game holds the
      // same reference and patches it with Object.assign, so replacing it here
      // silently split the menu's view of the settings from the game's.
      Object.assign(this.settings, { [key]: next })
      this.callbacks.onSettings({ [key]: next } as Partial<Settings>)
      // The same setting has a button on the menu and on the pause screen;
      // flipping either must update both.
      for (const node of this.toggles.get(key) ?? []) node.classList.toggle('on', next)
    })

    const list = this.toggles.get(key)
    if (list) list.push(btn)
    else this.toggles.set(key, [btn])
    return btn
  }

  private buildControls(): HTMLElement {
    const wrapper = el('div', 'controls')
    wrapper.append(
      buildControlColumn('p1', 'КЛАВИАТУРА', (def) => def.keyboardLabel, 'W A S D'),
      buildControlColumn('p2', 'ГЕЙМПАД', (def) => def.padLabel, 'ЛЕВЫЙ СТИК'),
    )
    return wrapper
  }

  syncSettings(settings: Settings): void {
    this.settings = settings
    for (const [key, nodes] of this.toggles) {
      for (const node of nodes) node.classList.toggle('on', Boolean(settings[key]))
    }
  }
}

function buildControlColumn(
  cls: string,
  name: string,
  keyOf: (def: (typeof ABILITIES)[number]) => string,
  moveLabel: string,
): HTMLElement {
  const col = el('div', 'controls-col')
  const heading = el('h4', cls)
  heading.textContent = name
  const table = document.createElement('table')

  table.append(controlRow('ДВИЖЕНИЕ', moveLabel, 'Идти в сторону нажатия'))
  for (const def of ABILITIES) {
    table.append(controlRow(def.name, keyOf(def), def.description))
  }

  col.append(heading, table)
  return col
}

function controlRow(label: string, key: string, description: string): HTMLTableRowElement {
  const tr = document.createElement('tr')
  const name = document.createElement('td')
  name.textContent = label
  name.className = 'ctl-name'
  const bind = document.createElement('td')
  bind.textContent = key
  bind.className = 'ctl-key'
  const hint = document.createElement('td')
  hint.textContent = description
  hint.className = 'ctl-hint'
  tr.append(name, bind, hint)
  return tr
}



/**
 * Rasterises an arena's SDF into a small canvas. Sampling the same function the
 * simulation uses guarantees the thumbnail can never lie about the shape.
 */
function renderArenaThumbnail(def: ArenaDef): HTMLCanvasElement {
  const width = 164
  const height = 62
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  const image = ctx.createImageData(width, height)

  const scale = Math.max(def.extentX / (width / 2), def.extentZ / (height / 2)) * 1.08

  for (let py = 0; py < height; py++) {
    for (let px = 0; px < width; px++) {
      const x = def.centerX + (px - width / 2) * scale
      const z = def.centerZ + (py - height / 2) * scale
      const d = def.shape.distance(x, z)
      const i = (py * width + px) * 4

      if (d < 0) {
        // Brighter near the rim so the silhouette reads at thumbnail size.
        const edge = Math.min(1, -d / (scale * 4))
        const shade = 96 - edge * 34
        image.data[i] = shade * 0.95
        image.data[i + 1] = shade
        image.data[i + 2] = shade * 0.82
        image.data[i + 3] = 255
      } else {
        image.data[i + 3] = 0
      }
    }
  }
  ctx.putImageData(image, 0, 0)
  return canvas
}
